import { BehaviorSubject, Observable, Subject, EMPTY } from 'rxjs';
import * as screenfull from 'screenfull';

import { Platform } from '@angular/cdk/platform';

import { SnPlayerState } from '@lib/player/common';

import { SnVideoPlayerType } from './video-player.enums';

export abstract class SnVideoPlayerReference {
  public rewindInterval: any;
  public rewindSpeedFactor: number = 0;
  public isFastForwarding: boolean = false;
  public isRewinding: boolean = false;
  public actualTime: number;
  public loaded: boolean = false;
  public rewindSpeed: number = 0.1;
  public fragLoading: boolean = false;
  public fragLoaded: boolean = false;

  get isPlaying(): boolean {
    return !this.videoElement.paused;
  }

  get rewinding$(): Observable<void> {
    return this._rewinding$.asObservable();
  }

  get rewindStop$(): Observable<void> {
    return this._rewindStop$.asObservable();
  }

  get rewindSpeedFactorChange$(): Observable<number> {
    return this._rewindSpeedFactorChange$.asObservable();
  }

  get destroyed$(): Observable<void> {
    return this._destroyed$.asObservable();
  }

  get fragChanged$(): Observable<void> {
    return this.getFragChangedObservable() || EMPTY;
  }

  get videoResolution(): any {
    return {
      width: this.videoElement?.videoWidth,
      height: this.videoElement?.videoHeight,
    };
  }

  private readonly _screenfull = screenfull;

  private _live: boolean;
  private _videoElement: HTMLVideoElement;
  private readonly _videoEventListeners = new Map<string, EventListenerOrEventListenerObject>();
  private readonly _rewinding$ = new Subject<void>();
  private readonly _rewindStop$ = new Subject<void>();
  private readonly _rewindSpeedFactorChange$ = new Subject<number>();
  private readonly _destroyed$ = new Subject<void>();

  private _splashString: string;

  protected constructor(
    public readonly type: SnVideoPlayerType,
    public readonly playerElement: HTMLElement,
    private readonly _platform: Platform,
    private readonly _state$: BehaviorSubject<SnPlayerState>,
    private readonly _currentTime$: BehaviorSubject<number>,
    private readonly _bufferTime$: BehaviorSubject<number>,
    private readonly _totalTime$: BehaviorSubject<number>,
    private readonly _volume$: BehaviorSubject<number>,
    private readonly _played$: BehaviorSubject<any>,
    private readonly _loaded$: Subject<boolean>,
    private readonly _ended: Subject<void>,
  ) {}

  get volume(): number {
    return this.videoElement.volume;
  }
  set volume(value: number) {
    if (this.videoElement) {
      this.videoElement.volume = value;
    }
  }

  get playbackRate(): number {
    return this._videoElement.playbackRate;
  }
  set playbackRate(value: number) {
    if (this._videoElement) {
      this._videoElement.playbackRate = value;
    }
  }

  set splashString(value: string) {
    if (this.videoElement) {
      this.videoElement.poster = value;
    }
  }

  get currentTime(): number {
    return this._videoElement.currentTime;
  }
  set currentTime(value: number) {
    if (this._videoElement) {
      this._videoElement.currentTime = value;
    }
  }

  get live(): boolean {
    return this._live;
  }
  set live(value: boolean) {
    this._live = value;
  }

  set muted(value: boolean) {
    if (this._videoElement) {
      this._videoElement.muted = value;
    }
    if (value) {
      this._volume$.next(0);
    }
  }

  protected get videoElement(): HTMLVideoElement {
    return this._videoElement;
  }

  protected set videoElement(value: HTMLVideoElement) {
    this._videoElement = value;
    this._volume$.next(this._videoElement.volume);

    if (this._splashString) {
      this._videoElement.poster = this._splashString;
    }

    this._initVideoEvents();
  }

  protected getFragChangedObservable(): Observable<void> | null {
    return null; // Default: no fragment changes for non-HLS players
  }

  public destroy(): void {
    this._destroyVideoEventListeners();
    clearInterval(this.rewindInterval);
    this.rewindInterval = null;
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  public play(): void {}

  public pause(): void {}

  public fastForward(): void {
    this.stopRewinding();
    this.rewindSpeedFactor = 0;
    if (!this.isPlaying) {
      this.play();
    }
    this.isFastForwarding = true;
    switch (this.playbackRate) {
      case 1:
        this.playbackRate = 2;
        break;
      case 2:
        this.playbackRate = 4;
        break;
      case 4:
        this.playbackRate = 8;
        break;
      case 8:
        this.playbackRate = 1;
        this.isFastForwarding = false;
        break;
    }
  }

  public rewindVideo(rewindAmount: number, speedFactor: number): void {
    if (this.fragLoading) {
      return;
    }
    const currentTime = this.videoElement.currentTime;
    let targetTime = currentTime - rewindAmount * speedFactor;
    if (targetTime < 0) {
      targetTime = 0;
    }
    this.videoElement.currentTime = targetTime;
    if (targetTime <= 0) {
      this.pauseRewinding();
    }
  }

  public pauseRewinding(): void {
    if (this.rewindInterval) {
      clearInterval(this.rewindInterval);
      this.rewindInterval = null;
    }
  }

  public startRewinding(rewindAmount: number = 0.1, speedFactor: number = 1): void {
    this.isRewinding = true;
    this.pauseRewinding();
    this.rewindInterval = setInterval(() => {
      this.rewindVideo(rewindAmount, speedFactor);
      this._rewinding$.next();
    }, 100);
  }

  public stopRewinding(): void {
    this.pauseRewinding();
    this.isRewinding = false;
    this._rewindStop$.next();
  }

  public rewind(speedFactor?: number): void {
    this.pause();
    this.isFastForwarding = false;
    this.playbackRate = 1;
    if (speedFactor) {
      this.rewindSpeedFactor = speedFactor;
    } else {
      switch (this.rewindSpeedFactor) {
        case 0:
          this.rewindSpeedFactor = 1;
          break;
        case 1:
          this.rewindSpeedFactor = 2;
          break;
        case 2:
          this.rewindSpeedFactor = 4;
          break;
        case 4:
          this.rewindSpeedFactor = 8;
          break;
        case 8:
          this.rewindSpeedFactor = 0;
          this._rewindSpeedFactorChange$.next(this.rewindSpeedFactor);
          this.stopRewinding();
          this.play();
          return;
      }
    }
    this.startRewinding(this.rewindSpeed, this.rewindSpeedFactor);
    this._rewindSpeedFactorChange$.next(this.rewindSpeedFactor);
  }

  public resetPlayback(): void {
    this.playbackRate = 1;
    this.rewindSpeedFactor = 0;
    clearInterval(this.rewindInterval);
    this.rewindInterval = null;
  }

  public toggleFullscreen(fullscreen: boolean): void {
    const element = this._platform.IOS ? this.videoElement : this.playerElement;
    if (this._screenfull.isEnabled) {
      if (
        this._screenfull.isFullscreen &&
        !this._screenfull.element.id.includes('fs__panorama-container')
      ) {
        this._screenfull.exit();
      } else {
        this._screenfull.request(element);
      }
    } else {
      if (fullscreen) {
        element.classList.add('fullscreen');
      } else {
        element.classList.remove('fullscreen');
      }
    }
  }

  private _initVideoEvents(): void {
    this._videoEventListeners.set('play', () => this._state$.next(SnPlayerState.Playing));
    this._videoEventListeners.set('pause', () => this._state$.next(SnPlayerState.Pause));
    this._videoEventListeners.set('seeked', () => (this.loaded = false));
    this._videoEventListeners.set('canplaythrough', () => {
      if (!this.loaded) {
        this.loaded = true;
        this._loaded$.next(this.loaded);
      }
    });
    this._videoEventListeners.set('ended', () => {
      this._ended.next();
      this._state$.next(SnPlayerState.Ended);
    });
    this._videoEventListeners.set('volumechange', () => {
      if (!this._videoElement.muted) {
        this._volume$.next(this._videoElement.volume);
      }
    });
    this._videoEventListeners.set('progress', () => this._onBuffered());
    this._videoEventListeners.set('timeupdate', () => {
      this._currentTime$.next(this._videoElement.currentTime);
      this._played$.next(this._videoElement.played);
      if (this._live) {
        this._totalTime$.next(this._videoElement.currentTime);
      }
    });
    this._videoEventListeners.set('durationchange', () => {
      this._totalTime$.next(this._videoElement.duration);
    });

    this._videoEventListeners.forEach((listener, event) => {
      this._videoElement.addEventListener(event, listener);
    });
  }

  private _destroyVideoEventListeners(): void {
    this._videoEventListeners.forEach((listener, event) => {
      this._videoElement.removeEventListener(event, listener);
    });
  }

  private _onBuffered(): void {
    const buffered = this._videoElement.buffered;
    const end = buffered.length - 1;
    if (end >= 0) {
      this._bufferTime$.next(buffered.end(end));
    }
  }
}
